// Modo vendedor (decisión D60): el preventista entra a la tienda con su usuario del personal y les toma el pedido a los
// comercios de su cartera en el local. Acá: "Mis visitas de hoy", el check-in con el GPS, "No compró" y lo que quedó
// guardado sin señal.
// - Sin señal, el check-in, "No compró" y el pedido quedan en el celular (por vendedor y por tienda) con la hora en que
//   pasaron, y se mandan solos cuando vuelve la señal, en el orden en que se hicieron. El pedido lleva su clave: aunque
//   se mande dos veces (la respuesta se perdió), la API devuelve el mismo y no lo duplica.
// - Lo que la API rechaza (el cliente ya no está en la cartera, un artículo que no se puede pedir) queda marcado para
//   que el preventista lo mire: se vuelve a intentar o se descarta. Precios, stock y fecha de entrega son los de cuando
//   llega; si la fecha elegida ya pasó, va la primera que haya.
import { create } from 'zustand';
import { type MotivoNoCompra, uuidv7 } from '../compartido';
import { borrar, guardar, leer } from './almacen';
import { api, ErrorApi, mensajeDeError, SinRed } from './api';
import { olvidarConsultas } from './consultas';
import { confirmarPedido, type PedidoNuevo } from './pedidos';
import { useSesion, vendedorDeSesion } from './sesion';
import { claveLocal } from './tienda-actual';
import type { PedidoDetalle, Visita, VisitasDelDia } from './tipos';

export interface Posicion {
  lat: number;
  lng: number;
  precision_m: number | null;
}

interface PendienteBase {
  /** En el pedido es también su clave: mandarlo dos veces no lo duplica. */
  id: string;
  cliente_id: string;
  cliente: string;
  /** Cuándo pasó en el celular: cuenta para la visita de ese día aunque llegue después. */
  hora: string;
  /** Lo que contestó la API si no lo aceptó (queda para que el preventista lo mire). */
  error?: string | null;
}

export type Pendiente =
  | (PendienteBase & { tipo: 'checkin'; posicion: Posicion | null })
  | (PendienteBase & {
      tipo: 'no_compro';
      posicion: Posicion | null;
      motivo: MotivoNoCompra;
      texto: string | null;
    })
  | (PendienteBase & { tipo: 'pedido'; pedido: PedidoNuevo; bultos: number; monto: number | null });

interface EstadoPendientes {
  vendedorId: string | null;
  lista: Pendiente[];
  enviando: boolean;
}

export const usePendientes = create<EstadoPendientes>(() => ({ vendedorId: null, lista: [], enviando: false }));

const clavePendientes = (vendedorId: string) => claveLocal(`vendedor:${vendedorId}:pendientes`);
const claveVisitas = (vendedorId: string) => claveLocal(`vendedor:${vendedorId}:visitas`);

function cargarPendientes(vendedorId: string | null): void {
  if (usePendientes.getState().vendedorId === vendedorId) return;
  usePendientes.setState({
    vendedorId,
    lista: vendedorId ? (leer<Pendiente[]>(clavePendientes(vendedorId)) ?? []) : [],
  });
}

function fijarLista(lista: Pendiente[]): void {
  const { vendedorId } = usePendientes.getState();
  if (!vendedorId) return;
  if (lista.length) guardar(clavePendientes(vendedorId), lista);
  else borrar(clavePendientes(vendedorId));
  usePendientes.setState({ lista });
}

/**
 * Guarda algo para mandar cuando vuelva la señal. Un check-in por cliente (vale el primero, como en la API); "No compró",
 * el último; el mismo pedido (la misma clave), una vez.
 */
export function encolar(p: Pendiente): void {
  cargarPendientes(vendedorDeSesion()?.id ?? null);
  const lista = usePendientes.getState().lista;
  if (p.tipo === 'checkin' && lista.some((x) => x.tipo === 'checkin' && x.cliente_id === p.cliente_id)) return;
  if (lista.some((x) => x.id === p.id)) return;
  fijarLista([
    ...lista.filter((x) => !(p.tipo === 'no_compro' && x.tipo === 'no_compro' && x.cliente_id === p.cliente_id)),
    p,
  ]);
}

export function descartarPendiente(id: string): void {
  fijarLista(usePendientes.getState().lista.filter((p) => p.id !== id));
}

/** Volver a intentar uno que la API rechazó (después de arreglar lo que decía). */
export function reintentarPendiente(id: string): Promise<void> {
  fijarLista(usePendientes.getState().lista.map((p) => (p.id === id ? { ...p, error: null } : p)));
  return enviarPendientes();
}

const cuerpoVisita = (clienteId: string, hora: string, posicion: Posicion | null) => ({
  cliente_id: clienteId,
  hora,
  ...(posicion && { lat: posicion.lat, lng: posicion.lng, precision_m: posicion.precision_m }),
});

type OyenteEnvio = (p: Pendiente, pedido: PedidoDetalle | null) => void;
const oyentesEnvio = new Set<OyenteEnvio>();
/** Avisa cada cosa guardada sin señal que llegó a la API (para el aviso "Mandamos el pedido de…"). */
export const alEnviarPendiente = (f: OyenteEnvio) => {
  oyentesEnvio.add(f);
  return () => {
    oyentesEnvio.delete(f);
  };
};

const fechaYaNoEsta = (e: unknown) =>
  e instanceof ErrorApi &&
  e.codigo === 'VALIDACION' &&
  !!(e.detalles as { fechas_entrega?: string[] } | undefined)?.fechas_entrega;

async function mandar(p: Pendiente): Promise<PedidoDetalle | null> {
  if (p.tipo === 'checkin') {
    await api<Visita>('/tienda/vendedor/visitas/checkin', { cuerpo: cuerpoVisita(p.cliente_id, p.hora, p.posicion) });
    return null;
  }
  if (p.tipo === 'no_compro') {
    try {
      await api<Visita>('/tienda/vendedor/visitas/no-compro', {
        cuerpo: { ...cuerpoVisita(p.cliente_id, p.hora, p.posicion), motivo: p.motivo, texto: p.texto },
      });
    } catch (e) {
      // Ese día ya tiene un pedido: "No compró" no corresponde y no hay nada que mandar.
      if (e instanceof ErrorApi && e.estado === 409) return null;
      throw e;
    }
    return null;
  }
  try {
    return await confirmarPedido(p.cliente_id, { ...p.pedido, hora: p.hora });
  } catch (e) {
    // La fecha elegida ya no se puede (pasó la hora de corte mientras no había señal): va la primera que haya.
    if (fechaYaNoEsta(e) && p.pedido.fecha_entrega)
      return confirmarPedido(p.cliente_id, { ...p.pedido, fecha_entrega: null, hora: p.hora });
    throw e;
  }
}

/** Sigue sin poder llegar (sin señal, el servidor caído o la sesión vencida): se intenta más tarde, sin marcar nada. */
const esperar = (e: unknown) =>
  e instanceof SinRed || (e instanceof ErrorApi && (e.estado === 401 || e.estado === 429 || e.estado >= 500));

let enCurso: Promise<void> | null = null;

/** Manda lo guardado, en orden. Una sola vez aunque lo pidan varias pantallas a la vez. */
export function enviarPendientes(): Promise<void> {
  enCurso ??= enviarAhora().finally(() => {
    enCurso = null;
    usePendientes.setState({ enviando: false });
  });
  return enCurso;
}

async function enviarAhora(): Promise<void> {
  const vendedor = vendedorDeSesion();
  if (!vendedor) return;
  cargarPendientes(vendedor.id);
  if (!usePendientes.getState().lista.some((p) => !p.error)) return;
  usePendientes.setState({ enviando: true });
  for (const p of [...usePendientes.getState().lista]) {
    if (p.error) continue;
    try {
      const pedido = await mandar(p);
      fijarLista(usePendientes.getState().lista.filter((x) => x.id !== p.id));
      olvidarConsultas(`pedidos:${p.cliente_id}`);
      if (pedido) olvidarConsultas(`frecuentes:${p.cliente_id}`);
      oyentesEnvio.forEach((f) => f(p, pedido));
    } catch (e) {
      if (esperar(e)) return;
      fijarLista(usePendientes.getState().lista.map((x) => (x.id === p.id ? { ...x, error: mensajeDeError(e) } : x)));
    }
  }
}

// ---------- El GPS ----------

/** La posición de ahora, o null si no hay GPS, no dio permiso o tardó demasiado (se registra igual, sin distancia). */
export function tomarPosicion(timeoutMs = 10_000): Promise<Posicion | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((listo) => {
    navigator.geolocation.getCurrentPosition(
      (p) => listo({ lat: p.coords.latitude, lng: p.coords.longitude, precision_m: Math.round(p.coords.accuracy) }),
      () => listo(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}

// ---------- La visita ----------

export interface ResultadoVisita {
  /** La respuesta de la API; null si quedó guardado sin señal. */
  visita: Visita | null;
  guardado: boolean;
  sinGps: boolean;
}

/** Check-in en el local: la hora y, con el GPS, la distancia al local. Sin señal queda guardado. */
export async function hacerCheckin(c: { id: string; nombre: string }): Promise<ResultadoVisita> {
  const posicion = await tomarPosicion();
  const hora = new Date().toISOString();
  try {
    const visita = await api<Visita>('/tienda/vendedor/visitas/checkin', {
      cuerpo: cuerpoVisita(c.id, hora, posicion),
    });
    return { visita, guardado: false, sinGps: !posicion };
  } catch (e) {
    if (!(e instanceof SinRed)) throw e;
    encolar({ id: uuidv7(), tipo: 'checkin', cliente_id: c.id, cliente: c.nombre, hora, posicion });
    return { visita: null, guardado: true, sinGps: !posicion };
  }
}

/** El cliente no compró: el motivo (y, si es "otro", cuál). Sin señal queda guardado. */
export async function marcarNoCompro(
  c: { id: string; nombre: string },
  motivo: MotivoNoCompra,
  texto: string | null,
): Promise<ResultadoVisita> {
  const posicion = await tomarPosicion(6_000);
  const hora = new Date().toISOString();
  try {
    const visita = await api<Visita>('/tienda/vendedor/visitas/no-compro', {
      cuerpo: { ...cuerpoVisita(c.id, hora, posicion), motivo, texto },
    });
    return { visita, guardado: false, sinGps: !posicion };
  } catch (e) {
    if (!(e instanceof SinRed)) throw e;
    encolar({ id: uuidv7(), tipo: 'no_compro', cliente_id: c.id, cliente: c.nombre, hora, posicion, motivo, texto });
    return { visita: null, guardado: true, sinGps: !posicion };
  }
}

/** El pedido que no se pudo mandar por falta de señal: queda guardado con su clave y la hora en que se tomó. */
export function guardarPedido(
  c: { id: string; nombre: string },
  pedido: PedidoNuevo,
  totales: { bultos: number; monto: number | null },
): void {
  encolar({
    id: pedido.clave,
    tipo: 'pedido',
    cliente_id: c.id,
    cliente: c.nombre,
    hora: pedido.hora ?? new Date().toISOString(),
    pedido: { ...pedido, hora: undefined },
    ...totales,
  });
}

// ---------- Mis visitas de hoy ----------

/** La lista del día. Sin señal, la última que se trajo en este celular (con `guardada: true`). */
export async function cargarVisitas(): Promise<VisitasDelDia & { guardada?: boolean }> {
  const vendedor = vendedorDeSesion();
  try {
    const v = await api<VisitasDelDia>('/tienda/vendedor/visitas');
    if (vendedor) guardar(claveVisitas(vendedor.id), v);
    return v;
  } catch (e) {
    const previa = vendedor ? leer<VisitasDelDia>(claveVisitas(vendedor.id)) : null;
    if (e instanceof SinRed && previa) return { ...previa, guardada: true };
    throw e;
  }
}

export interface EstadoLocal {
  estado: 'por_visitar' | 'en_el_local' | 'con_pedido' | 'no_compro';
  /** Lo último que hizo quedó guardado sin señal. */
  sinMandar: boolean;
}

/** Cómo va la visita contando lo que todavía no se mandó. */
export function estadoConPendientes(
  clienteId: string,
  visita: Pick<Visita, 'estado'> | null,
  pendientes: Pendiente[],
): EstadoLocal {
  const suyos = pendientes.filter((p) => p.cliente_id === clienteId);
  const actual = visita?.estado ?? 'por_visitar';
  if (suyos.some((p) => p.tipo === 'pedido')) return { estado: 'con_pedido', sinMandar: true };
  if (actual === 'con_pedido') return { estado: actual, sinMandar: false };
  if (suyos.some((p) => p.tipo === 'no_compro')) return { estado: 'no_compro', sinMandar: true };
  if (actual === 'por_visitar' && suyos.some((p) => p.tipo === 'checkin'))
    return { estado: 'en_el_local', sinMandar: true };
  return { estado: actual, sinMandar: false };
}

// ---------- Al abrir la app ----------

let iniciado = false;

/**
 * Se llama una vez al arrancar: con la sesión de un preventista toma lo guardado de ese preventista y lo manda al
 * volver la señal, al volver a la app y cada medio minuto mientras quede algo.
 */
export function iniciarVendedor(): void {
  if (iniciado || typeof window === 'undefined') return;
  iniciado = true;
  const revisar = () => {
    const id = useSesion.getState().sesion?.vendedor?.id ?? null;
    cargarPendientes(id);
    if (id) void enviarPendientes();
  };
  useSesion.subscribe((s, previo) => {
    if (s.sesion?.vendedor?.id !== previo.sesion?.vendedor?.id) revisar();
  });
  revisar();
  window.addEventListener('online', () => void enviarPendientes());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void enviarPendientes();
  });
  setInterval(() => {
    if (usePendientes.getState().lista.some((p) => !p.error) && navigator.onLine !== false) void enviarPendientes();
  }, 30_000);
}
